<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import ConeSvg from '@svg/new-cone.svg'
import LockSvg from '@svg/new-lock.svg'
import PauseSvg from '@svg/new-pause.svg'
import PlaySvg from '@svg/new-play.svg'
import CheckSvg from '@svg/new-check.svg'

const props = defineProps({
    course: Object,
    episode: Object,
    selected: Boolean,
    progress: Number,
    identifier: String
})

const complete = computed(() => props.progress === 100)
</script>

<template>
    <component :is="episode.upcoming ? `div` : Link" :href="`/watch/${course.slug}/${episode.slug}`" :only="['currentEpisode', 'episodes', 'transcript']" class="rounded-3xl min-h-16 py-2 w-full flex items-center space-x-4 px-3.5 bg-gradient-to-r group/main" :class="{ 'from-[rgba(54,47,150,1)] to-[rgba(54,47,150,1)]': selected, 'hover:from-[rgba(83,75,191,0.2)] hover:to-[rgba(83,75,191,0.2)]': !selected && !episode.upcoming }" :id="`${identifier}-episode-${episode.id}`" :title="episode.title">
        <div class="rounded-full size-[40px] flex items-center justify-center shrink-0" :class="{ 'bg-[rgba(22,31,56,0.5)]': selected, 'bg-[rgba(29,30,65,1)]': !selected }">
            <template v-if="episode.upcoming">
                <ConeSvg class="size-5 fill-[rgba(255,205,75,1)] drop-shadow-[2px_3px_5px_rgba(255,205,75,0.6)]" />
            </template>
            <template v-else-if="!episode.vimeo_id">
                <LockSvg class="size-6 text-[rgba(142,131,246,1)] drop-shadow-[2px_3px_5px_rgba(142,131,246,0.6)] ml-px" />
            </template>
            <template v-else>
                <PauseSvg v-if="selected" class="size-8 fill-[rgba(142,131,246,1)] drop-shadow-[2px_3px_5px_rgba(142,131,246,0.6)] ml-[5px] mt-[4px]" />
                <PlaySvg v-if="!selected" class="size-4 fill-[rgba(65,251,220,1)] drop-shadow-[2px_3px_5px_rgba(26,188,151,0.8)] ml-[3px]" />
            </template>
        </div>
        <div class="grow">
            <div class="text-[rgba(233,232,255,1)] font-semibold text-sm md:leading-5 relative line-clamp-2" :class="{ 'after:to-[rgba(54,47,150,1)]': selected, 'group-hover/main:after:to-[rgba(32,33,81,1)]': !selected && !episode.upcoming, 'after:to-[rgba(20,22,57,1)]': !selected }">
                {{ episode.order.toString().length === 1 ? `0${episode.order}` : `${episode.order}` }}. {{ episode.title }}
            </div>
            <div class="text-xs leading-none mt-1.5" :class="{ 'text-[rgba(255,255,255,0.4)]': selected, 'text-[rgba(96,106,146,1)]': !selected }">
                {{ episode.upcoming ? `Soon` : episode.duration }}
            </div>
        </div>
        <div class="shrink-0" v-if="!episode.upcoming">
            <div class="relative size-[42px] rounded-full border-[2px] mx-auto flex items-center justify-center text-[0.75rem] font-semibold [text-shadow:2px_2px_10px_rgba(177,125,254,0.8)] text-[rgba(177,125,254,1)] before:absolute before:rounded-full before:-top-[2px] before:-left-[2px] before:-right-[2px] before:-bottom-[2px] before:[mask:radial-gradient(farthest-side,#0000_calc(99%-2px),var(--ring-color)_calc(100%-2px))] before:[background:conic-gradient(var(--ring-color)_calc(var(--value)*1%),#0000_0)]" :style="`--value: ${progress}; --ring-color: ${ complete ? 'rgba(65,251,220,1)' : (selected ? 'rgba(255,255,255,1)' : 'rgba(132,156,241,1)') };`" :class="{ 'border-[rgba(32,44,81,0.5)] text-white': selected, 'border-[rgba(32,44,81,1)] text-[rgba(186,201,255,1)]': !selected, 'opacity-20': !selected }">
                <template v-if="complete">
                    <CheckSvg class="size-7 text-[rgba(65,251,220,1)] drop-shadow-[2px_3px_5px_rgba(26,188,151,0.8)]" />
                </template>
                <template v-else>
                    {{ progress }}%
                </template>
            </div>
        </div>
    </component>
</template>
